import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import { MapPin, Zap, TrendingUp, ChevronRight } from 'lucide-react';
import portfolioResidential01 from '../../assets/portfolio-residential-01.png';
import portfolioCommercial01 from '../../assets/portfolio-commercial-01.png';
import portfolioCoOwnership01 from '../../assets/portfolio-coownership-01.png';
import portfolioResidential03 from '../../assets/portfolio-residential-03.png';

export const Portfolio = () => {
  const { t } = useLanguage();

  const projectImages = [
    portfolioResidential01,
    portfolioCommercial01,
    portfolioCoOwnership01,
    portfolioResidential03,
  ];

  const projects = t('portfolio.projects');

  return (
    <section id="portfolio" className="py-20 md:py-32 bg-white" data-testid="portfolio-section">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-outfit text-4xl md:text-5xl font-semibold tracking-tight text-stone-900 mb-4">
            {t('portfolio.title')}
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            {t('portfolio.subtitle')}
          </p>
        </motion.div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group relative bg-white rounded-2xl overflow-hidden border border-stone-100 hover:shadow-2xl transition-all duration-500"
              data-testid={`portfolio-project-${index}`}
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={projectImages[index]}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

                {/* Type Badge */}
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-stone-800 px-3 py-1 rounded-full text-sm font-medium">
                  {project.type}
                </div>

                {/* Title Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-outfit text-xl md:text-2xl font-semibold text-white mb-1">
                    {project.title}
                  </h3>
                  <div className="flex items-center gap-2 text-white/80 text-sm">
                    <MapPin className="w-4 h-4" />
                    <span>{project.location}</span>
                  </div>
                </div>
              </div>

              {/* Details */}
              <div className="p-6">
                <p className="text-stone-600 mb-4">{project.description}</p>

                <div className="flex items-center justify-between">
                  <div className="flex gap-6">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                        <Zap className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-stone-500">{t('portfolio.power')}</p>
                        <p className="font-semibold text-stone-900">{project.power}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-xs text-stone-500">{t('portfolio.savings')}</p>
                        <p className="font-semibold text-stone-900">{project.savings}</p>
                      </div>
                    </div>
                  </div>

                  {/*<button className="flex items-center gap-1 text-green-700 font-medium text-sm hover:gap-2 transition-all">
                    {t('portfolio.viewDetails')}
                    <ChevronRight className="w-4 h-4" />
                  </button>*/}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
